/**
 * AI CONTEXT
 * Purpose: internal tryout debug page for progress/enrollment consistency checks.
 * Key deps: TryoutProgressFormModal, useOneDoc/useSomeDocs, attendance/session checks.
 * Referenced by: App route ROUTES.TEST (debug/permission-gated).
 * Importance: validates complex tryout side effects quickly.
 * Refactor: move hard-coded IDs to debug config inputs/defaults.
 */
import { Button, Card, Tag, Typography } from "antd"
import dayjs from "dayjs"
import React, { useMemo, useState } from "react"

import {
  classAtoms,
  classSessionAtoms,
  studentAtoms,
  tryoutAtoms,
} from "#atoms"
import Flex from "#blocks/Flex"
import Page from "#blocks/Page"
import AttendanceIcon from "#components/AttendanceIcon"
import PortfolioManagementModal from "#components/PortfolioManagement/PortfolioManagementModal"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { Uuid } from "#customTypes/globalTypes"
import TryoutProgressFormModal from "#forms/TryoutProgressForm/TryoutProgressFormModal"
import { useOneDoc, useSomeDocs } from "#hooks/jotai/useSubscription"

const { Text, Title } = Typography

const DEFAULT_TRYOUT_ID = "yTTjZy4EUrzmhLdpw9Iy"

const YesNo = ({ value }: { value: boolean }) => (
  <Tag color={value ? "green" : "red"}>{value ? "YES" : "NO"}</Tag>
)

// ─── Enrollment Session Grid ───

const EnrollmentSessionGrid = ({
  studentId,
  classId,
}: {
  studentId: Uuid
  classId: Uuid
}) => {
  const student = useOneDoc(studentAtoms, DOC_TYPES.STUDENTS, studentId)
  const classDoc = useOneDoc(classAtoms, DOC_TYPES.CLASSES, classId)
  const sessionIds = useMemo(
    () => classDoc?.sessionIds ?? [],
    [classDoc?.sessionIds]
  )
  const sessions = useSomeDocs(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    sessionIds
  )

  const attendance = student?.attendance ?? {}
  const classAttendance = attendance[classId]

  if (!student || !classDoc || !sessions.length) {
    return <Text type="secondary">Loading...</Text>
  }

  return (
    <Flex gap={6} style={{ flexWrap: "wrap" }}>
      {sessions.map((session, idx) => {
        const att = classAttendance?.[idx]
        const status = att?.status ?? ATTENDANCE_STATUSES.NOT_ENROLLED
        const inRoster = session.studentIds?.includes(studentId) ?? false
        const date = dayjs(session.eventStart).format("M/D")

        return (
          <Flex
            key={session.id}
            col
            center
            gap={2}
            style={{
              border: "1px solid #d9d9d9",
              borderRadius: 6,
              padding: "4px 6px",
              minWidth: 56,
              fontSize: 11,
            }}
          >
            <Text strong style={{ fontSize: 11 }}>
              {date}
            </Text>
            <AttendanceIcon status={status} size="small" />
            <Tag
              color={inRoster ? "green" : "red"}
              style={{ fontSize: 10, margin: 0, lineHeight: "16px" }}
            >
              {inRoster ? "In" : "Out"}
            </Tag>
          </Flex>
        )
      })}
    </Flex>
  )
}

// ─── Main ───

const Test = () => {
  const [tryoutId, setTryoutId] = React.useState(DEFAULT_TRYOUT_ID)
  const [isOpen, setIsOpen] = useState(true)
  const [isPortfolioOpen, setIsPortfolioOpen] = useState(true)

  const tryout = useOneDoc(tryoutAtoms, DOC_TYPES.TRYOUTS, tryoutId)

  const studentId = tryout?.studentId
  const tryoutSessionId = tryout?.sessionId
  const enrollmentClassId = (
    tryout?.step3 as { selectedClassId?: Uuid } | undefined
  )?.selectedClassId

  // Subscriptions
  const student = useOneDoc(studentAtoms, DOC_TYPES.STUDENTS, studentId)
  const tryoutSession = useOneDoc(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    tryoutSessionId
  )
  const enrollmentClass = useOneDoc(
    classAtoms,
    DOC_TYPES.CLASSES,
    enrollmentClassId
  )

  const tryoutInSession = tryoutSession?.tryoutIds?.includes(tryoutId) ?? false
  const studentInClass =
    enrollmentClass?.studentIds?.includes(studentId ?? "") ?? false

  return (
    <Page>
      <Flex
        col
        gap={12}
        style={{
          height: "90vh",
          overflow: "auto",
          paddingTop: 100,
          paddingInline: 24,
          maxWidth: 800,
        }}
      >
        {isPortfolioOpen && (
          <PortfolioManagementModal
            isOpen={isPortfolioOpen}
            handleCloseAttempt={() => setIsPortfolioOpen(false)}
            title="AP Portfolio Progression Mock"
            initialValues={{ id: "portfolio-mock-001" }}
          />
        )}

        {isOpen && (
          <TryoutProgressFormModal
            tryoutId={tryoutId}
            onClose={() => setIsOpen(false)}
          />
        )}

        <Flex gap={8} style={{ alignItems: "center" }}>
          <Title level={4} style={{ margin: 0 }}>
            Portfolio Mock
          </Title>
          <Button
            size="small"
            type="default"
            onClick={() => setIsPortfolioOpen(true)}
          >
            Open Portfolio Mock
          </Button>
        </Flex>

        <Flex gap={8} style={{ alignItems: "center" }}>
          <Title level={4} style={{ margin: 0 }}>
            Tryout Debug
          </Title>
          <Button size="small" type="primary" onClick={() => setIsOpen(true)}>
            Open Form
          </Button>
        </Flex>

        <input
          value={tryoutId}
          onChange={(e) => setTryoutId(e.target.value)}
          style={{ fontFamily: "monospace", padding: 4, width: 320 }}
          placeholder="Tryout ID"
        />

        {!tryout && (
          <Text type="secondary">
            {tryoutId ? "Loading tryout..." : "Enter a Tryout ID."}
          </Text>
        )}

        {tryout && (
          <Card size="small" styles={{ body: { padding: 12 } }}>
            <Flex col gap={8}>
              <Flex gap={16} style={{ fontSize: 12, alignItems: "center" }}>
                <Text strong>{student?.name ?? "..."}</Text>
                <Text type="secondary">→</Text>
                <Text strong>
                  {enrollmentClass?.name ?? "No class selected"}
                </Text>
              </Flex>

              <Flex gap={16} style={{ alignItems: "center" }}>
                <Text style={{ fontSize: 12 }}>
                  Tryout in session.tryoutIds:
                </Text>
                <YesNo value={tryoutInSession} />

                {enrollmentClassId && (
                  <>
                    <Text style={{ fontSize: 12 }}>
                      Student in class.studentIds:
                    </Text>
                    <YesNo value={studentInClass} />
                  </>
                )}
              </Flex>

              {studentId && enrollmentClassId && (
                <EnrollmentSessionGrid
                  studentId={studentId}
                  classId={enrollmentClassId}
                />
              )}
            </Flex>
          </Card>
        )}
      </Flex>
    </Page>
  )
}

export default Test
